import type { IPeerConnection } from '../types';

export type PeerStats = {
  peerId: string;
  timestamp: number;
  bitrate: number;
  packetLoss: number;
  roundTripTime: number;
  framesPerSecond: number;
  jitter: number;
  connectionType: string;
};

/**
 * PeerStatsManager - Collects and batches WebRTC stats from peer connections.
 *
 * Periodically polls RTCPeerConnection.getStats() and parses the report
 * into a normalized PeerStats structure. Stats are batched and can be
 * reported upstream.
 */
export class PeerStatsManager {
  private statsIntervals = new Map<string, ReturnType<typeof setInterval>>();
  private latestStats = new Map<string, PeerStats>();
  private onStatsUpdated: ((stats: PeerStats) => void) | null = null;
  private pollIntervalMs: number;

  constructor(pollIntervalMs = 2000) {
    this.pollIntervalMs = pollIntervalMs;
  }

  public setOnStatsUpdated(callback: (stats: PeerStats) => void): void {
    this.onStatsUpdated = callback;
  }

  public startCollection(peerId: string, connection: IPeerConnection): void {
    this.stopCollection(peerId);

    const interval = setInterval(async () => {
      const stats = await this.collectStats(peerId, connection);
      if (stats) {
        this.latestStats.set(peerId, stats);
        this.onStatsUpdated?.(stats);
      }
    }, this.pollIntervalMs);

    this.statsIntervals.set(peerId, interval);
  }

  public stopCollection(peerId: string): void {
    const interval = this.statsIntervals.get(peerId);
    if (interval) {
      clearInterval(interval);
      this.statsIntervals.delete(peerId);
    }
    this.latestStats.delete(peerId);
  }

  public getLatestStats(peerId: string): PeerStats | undefined {
    return this.latestStats.get(peerId);
  }

  public getAllStats(): PeerStats[] {
    return Array.from(this.latestStats.values());
  }

  public destroy(): void {
    for (const interval of this.statsIntervals.values()) {
      clearInterval(interval);
    }
    this.statsIntervals.clear();
    this.latestStats.clear();
    this.onStatsUpdated = null;
  }

  private async collectStats(peerId: string, connection: IPeerConnection): Promise<PeerStats | null> {
    const rtcPc = connection.getRtcPeerConnection();
    if (!rtcPc) return null;

    try {
      const report = await rtcPc.getStats();
      return this.parseStatsReport(peerId, report);
    } catch (error) {
      console.warn(`[PeerStatsManager] Failed to collect stats for ${peerId}:`, error);
      return null;
    }
  }

  private parseStatsReport(peerId: string, report: RTCStatsReport): PeerStats {
    let bitrate = 0;
    let packetLoss = 0;
    let roundTripTime = 0;
    let framesPerSecond = 0;
    let jitter = 0;
    let connectionType = 'unknown';

    report.forEach((stats) => {
      if (stats.type === 'outbound-rtp' && stats.kind === 'video') {
        bitrate = (stats as any).bytesSent ?? 0;
        framesPerSecond = (stats as any).framesPerSecond ?? 0;
      }
      if (stats.type === 'remote-inbound-rtp') {
        packetLoss = (stats as any).packetsLost ?? 0;
        roundTripTime = (stats as any).roundTripTime ?? 0;
        jitter = (stats as any).jitter ?? 0;
      }
      if (stats.type === 'candidate-pair' && (stats as any).state === 'succeeded') {
        connectionType = (stats as any).remoteCandidateType ?? 'unknown';
      }
    });

    return {
      peerId,
      timestamp: Date.now(),
      bitrate,
      packetLoss,
      roundTripTime,
      framesPerSecond,
      jitter,
      connectionType,
    };
  }
}
